<template>
  <div class="contentful-asset" v-if="data && data.file">
    <template v-if="isImage && !isGif">
      <div class="asset-image">
        <component 
        :is="link ? 'a' : 'div'" 
        :href="link" 
        :target="linkTarget"
        class="asset-image-inner" 
        :class="{hasProductTags}" 
        @click.stop="onImageClick"
        >
          <object v-if="isSVG" :data="data.file.url" type="image/svg+xml" :alt="data.title" />
          <ResponsiveImage
            v-else
            :lazy="lazy"
            :url="`${data.file.url}?w={width}&q={q}`"
            :alt="data.title"
            :skeleton="true"
            :width="data.file.details.image.width"
            :height="data.file.details.image.height"
            @mouseover="flashProductTags"
          />
          <template v-if="hasProductTags">
            <button class="product-tag-toggle-button button white filled" @click.stop="toggleProductsModal" v-intersection="flashProductTags">
              <ShoppingBag class="shopping-bag-icon" />
            </button>
            <template v-for="productTag in productTags" :key="productTag.productId">
            <ImageProductTag v-if="productTag && productMap && productMap[productTag.productId]"
              :bubble-location="productTag.bubble"
              :product="productMap[productTag.productId]"
              :coordinates="productTag.coordinate"
              :isTagVisible="isProductTagsVisible"
              :isAnchorVisible="showProductTagAnchors"
            />
            </template>
          </template>
        </component>
        <div class="description">
          <slot name="description-override"
            ><span>{{ data.description }}</span></slot
          >
        </div>
      </div>
    </template>
    <template v-else-if="isVideo || isGif">
      <div class="asset-video">
        <component :is="link ? 'a' : 'div'" :href="link" :target="linkTarget">
          <video
            ref="videoElement"
            :src="`${data.file.url}${isGif ? '?fm=mp4' : ''}`"
            playsinline
            :controls="!isAutoplay"
            :disableRemotePlayback="isAutoplay"
            :loop="isAutoplay"
            :muted="isAutoplay"
            :autoplay="shouldAutoplay"
            :poster="videoPosterUrl"
          ></video>
        </component>
      </div>
    </template>
    <template v-else>
      <div class="button-text">
        <a :href="data.file.url" target="_blank">{{ data.title }}</a>
      </div>
    </template>
  </div>
</template>

<script>
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import { computed, ref, watch, onMounted, toRefs, defineAsyncComponent,reactive } from 'vue';
import { fetchProductsByIds } from "@/compositions/useProduct.js";
import Intersection from '@/components/commons/Intersection.js';
import { openModal, closeModal, state as modalState} from '@/components/commons/Dialog/Dialog.vue';
import useResize from '@/compositions/useResize.js';
import { useRouter } from 'vue-router';

export default {
  components: {
    ResponsiveImage,
    ImageProductTag: defineAsyncComponent(() => import('@/components/commons/ImageProductTag.vue')),
    ShoppingBag: defineAsyncComponent(() => import('@/assets/ShopBy/shopping-bag.svg')),
  },
  props: {
    ...contentfulProps,
    isHibernated:{
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    link:{
      type: String,
      default: ""
    },
    linkTarget: {
      type: String,
      default: '_self',
    },
    autoplayVideo:{
      type: Boolean,
      default: true
    },
    videoPosterUrl:{
      type: String,
      default: ""
    },
    productTags:{
      type: Object,
      default: null
    }
  },
  directives: {
    intersection: Intersection,
  },
  async setup(props, ctx) {
    const { autoplayVideo, link, isHibernated, videoPosterUrl, productTags } = toRefs(props)


    const { data, system, init } = useContentful('asset', props, ctx);
    const isImage = computed(() => data.value?.file.contentType.startsWith('image'));
    const isVideo = computed(() => data.value?.file.contentType.startsWith('video'));
    const isSVG = computed(() => data.value?.file.contentType.toLowerCase() === 'image/svg+xml');
    const isGif = computed(() => data.value?.file.contentType.toLowerCase() === 'image/gif');

    const shouldAutoplay = computed(() => !isHibernated.value && isAutoplay.value);
    const isAutoplay = computed(()=>(isGif.value || autoplayVideo.value));

    const hasProductTags = computed(()=>productTags.value && productTags.value.length > 0)

    const showProductTags = ref(false);
    const showProductTagAnchors = ref(false);

    const videoElement = ref(null);

    const productMap = ref({});

    const { isMobile } = useResize();


    const loadProducts = async ()=>{
      if(!hasProductTags.value){
        return;
      }
      const ids = productTags.value.map(pt=>pt.productId);
      const idsNeedData = ids.filter(id => !productMap.value[id]);
      if(idsNeedData && idsNeedData.length){
        const products = await fetchProductsByIds(idsNeedData);
        products?.map(p=>productMap.value[p.hashKey] = p);
      }
    }

    let videoPlayPromise = null;
    const updateAutoplay = async ()=>{
      if (videoElement.value) {
          if (shouldAutoplay.value) {
            videoElement.value.muted = true;
            videoPlayPromise = new Promise((res,rej)=>{
              videoElement.value.play().then(()=>{
                videoPlayPromise = null;
                res();
              })
            })
          } else {
            videoElement.value.muted = false;
            if(videoPlayPromise){
              await videoPlayPromise;
            }
            videoElement.value.pause();
          }
        }
    };

    const productDialogUrlFragment = computed(()=>{
      return `product-list-${encodeURIComponent(system.value?.id)}`;
      // return `product-list-modal`;
    })

        const router = useRouter();

    watch(
      ()=>shouldAutoplay.value,
      (autoplay, prevAutoplay) => {
        updateAutoplay();
      },
    );

    watch(
      ()=>productTags.value,
      () => {
        loadProducts();
      },
    );

    watch(
      ()=>isMobile.value,
      () => {
        if(modalState.visible && modalState.urlFragment === productDialogUrlFragment.value){
          closeModal();
        }
      },
    );

    onMounted(()=>{
      updateAutoplay()
      if(router.currentRoute.value.hash === `#${productDialogUrlFragment.value}`){
        toggleProductsModal()
      }
    })


    const onImageClick = ()=>{
      if(isHibernated.value) return;
      if (hasProductTags.value){
        showProductTags.value = !showProductTags.value
      }
    }

    const flashProductTags = ()=>{
      if(isHibernated.value || !hasProductTags.value) return;
      showProductTagAnchors.value = true;
      window.setTimeout(()=>{
        showProductTagAnchors.value = false;
      },2000);
    }

    const toggleProductsModal = ()=>{
      if(isHibernated.value) return;
      if(!isMobile.value){
        onImageClick();
        return;
      }
      showProductTags.value = false;
      openModal(
        defineAsyncComponent(()=> import('@/components/commons/Dialog/ProductsModal.vue')),{
          title: data.value.title,
          products: productTags.value.map(p=>productMap.value[p.productId]).filter(x=>x)
        },{
          showClose: false, 
          urlFragment: productDialogUrlFragment.value
        }
      )
    }


    const isProductTagsVisible = computed(()=>!!productTags.value && productTags.value.length && showProductTags.value && Object.keys(productMap.value).length && true)

    await init()
    await loadProducts();

    return {
      isImage,
      isVideo,
      isSVG,
      isGif,
      shouldAutoplay,
      isAutoplay,
      data,
      videoElement,
      videoPosterUrl,
      link,
      productTags,
      hasProductTags,
      productMap,
      onImageClick,
      isProductTagsVisible,
      showProductTags,
      flashProductTags,
      showProductTagAnchors,
      toggleProductsModal,
    };
  },
};
</script>

<style lang="postcss">
.contentful-asset {
  width: 100%;
  height: 100%;

  .asset-video {
    width: 100%;
    height: calc(100% - 50px);
    video {
      width: 100%;
      height: 100%;
    }
  }

  .asset-image {
    width: 100%;
    height: 100%;

    a > object {
      display: block;
      pointer-events: none;
    }

    .image {
      width: 100%;
      height: calc(100% - 50px);
    }
    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
    .description {
      font-size: var(--font-size-12);
      line-height: 1.2em;
      color: var(--grey-medium);
      text-align: center;
      white-space: pre-wrap;
      max-width: 400px;
      margin: auto;
      margin-top: var(--space-2);
      padding-left: 20px;
      padding-right: 20px;
    }
  }


  .asset-image-inner{
    &.hasProductTags{
      position:relative;
      cursor:pointer;
      img {
        display: block;
      }

      .product-tag-toggle-button{
        position: absolute;
        left: 0;
        bottom: 0;
        border-radius: 50%;
        margin: var(--space-4);
        font-size: var(--font-size-16);
        width: 2em;
        height: 2em;
        padding: 0.5em !important;
        svg{
          width: 1em;
          height: 1em;
          margin: 0;
          margin-left: -1px;
          margin-top: -1px;
        }
      }
    }
  }
}
</style>
